'use client';

import { useRef, useState } from 'react';
import { BrowserFrame, PhoneFrame, Shot } from './Shot';
import { TOUR_TABS } from './landingContent';

const DeviceIcon = ({ kind }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    {kind === 'desktop'
      ? <path d="M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 14.5zM8 20h8M12 16v4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      : <path d="M8 3h8a1.5 1.5 0 0 1 1.5 1.5v15A1.5 1.5 0 0 1 16 21H8a1.5 1.5 0 0 1-1.5-1.5v-15A1.5 1.5 0 0 1 8 3zM10.5 18h3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
  </svg>
);

/**
 * Tabbed tour of the real product, on a desktop browser or as a fan of phone screens.
 * Every screenshot stays mounted so switching tabs or devices is instant.
 */
export default function ProductTour() {
  const [active, setActive] = useState(0);
  const [device, setDevice] = useState('desktop');
  const tabRefs = useRef([]);
  const tab = TOUR_TABS[active];
  const total = TOUR_TABS.length;

  const move = (next) => {
    const index = (next + total) % total;
    setActive(index);
    tabRefs.current[index]?.focus();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') { event.preventDefault(); move(active + 1); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); move(active - 1); }
    else if (event.key === 'Home') { event.preventDefault(); move(0); }
    else if (event.key === 'End') { event.preventDefault(); move(total - 1); }
  };

  return (
    <div className="tour">
      <div className="tour-top">
        <div className="tour-tabs" role="tablist" aria-label="Product tour" onKeyDown={onKeyDown}>
          {TOUR_TABS.map((t, i) => (
            <button
              key={t.id}
              ref={(el) => { tabRefs.current[i] = el; }}
              role="tab"
              id={`tour-tab-${t.id}`}
              aria-selected={i === active}
              aria-controls="tour-panel"
              tabIndex={i === active ? 0 : -1}
              className={`tour-tab${i === active ? ' is-active' : ''}`}
              onClick={() => setActive(i)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="device-toggle" role="group" aria-label="Device">
          {['desktop', 'phone'].map((d) => (
            <button key={d} type="button" aria-pressed={device === d} className={device === d ? 'is-active' : ''} onClick={() => setDevice(d)}>
              <DeviceIcon kind={d} /> {d === 'desktop' ? 'Desktop' : 'Phone'}
            </button>
          ))}
        </div>
      </div>

      <div className="tour-caption" aria-live="polite">
        <h3>{tab.title}</h3>
      </div>

      <div id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${tab.id}`} className={`tour-stage ${device}`}>
        <div className={`tour-desktop${device === 'desktop' ? ' is-shown' : ''}`} aria-hidden={device !== 'desktop'}>
          <BrowserFrame url={tab.url}>
            <div className="tour-stack">
              {TOUR_TABS.map((t, i) => (
                <div key={t.id} className={`tour-slide${i === active ? ' is-active' : ''}`} aria-hidden={i !== active}>
                  <Shot name={t.shot} alt={i === active && device === 'desktop' ? `${t.label}: ${t.title}` : ''} priority={i === 0} />
                </div>
              ))}
            </div>
          </BrowserFrame>
        </div>

        <div className={`tour-phones${device === 'phone' ? ' is-shown' : ''}`} aria-hidden={device !== 'phone'}>
          {TOUR_TABS.map((t, i) => {
            const offset = i - active;
            const near = Math.abs(offset) <= 1;
            return (
              <div
                key={t.id}
                className={`tour-phone${offset === 0 ? ' is-active' : ''}`}
                style={{ '--o': offset, '--abs': Math.abs(offset) }}
                data-near={near}
              >
                <button type="button" tabIndex={near && offset !== 0 && device === 'phone' ? 0 : -1} className="phone-hit" onClick={() => setActive(i)} aria-label={`Show ${t.label} on phone`} />
                <PhoneFrame>
                  <Shot name={t.phone} alt={offset === 0 && device === 'phone' ? `${t.label} on a phone` : ''} />
                </PhoneFrame>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
